import React, { useState, useEffect, useRef } from "react";
import api, { apiErr } from "../../lib/api";
import { CATEGORIES, FREQUENCIES } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { InfoTip } from "./InfoTip";
import { Calendar } from "../ui/calendar";
import { toast } from "sonner";
import { Sparkles, Upload, PenLine, CalendarIcon, Loader2, Info, FileText } from "lucide-react";
import { formatDate } from "../../lib/format";

const EMPTY = {
  name: "", merchant: "", category: "subscription", subcategory: "", amount: "",
  frequency: "monthly", next_due_date: "", payment_source: "", essential_flexible: "flexible",
  shared_individual: "individual", status: "active", notes: "", detection_source: "manual",
  total_instalments: "", instalments_completed: "", total_remaining_liability: "",
  user_share: "", people_sharing: "", reimbursement_status: "", confidence: "",
  share_members: [], reminder_note: "",
};

export default function AddCommitmentDialog({ open, onOpenChange, existing, onSaved, defaultDate }) {
  const [form, setForm] = useState(EMPTY);
  const [aiDetected, setAiDetected] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lowConfidence, setLowConfidence] = useState(false);
  const fileRef = useRef();
  const pdfRef = useRef();

  const extractPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("Please choose a PDF file."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("PDF must be under 10 MB."); return; }
    setExtracting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/extract/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyExtraction(data.result, "pdf");
      toast.success("We read your statement. Please review every field before saving.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setExtracting(false); if (pdfRef.current) pdfRef.current.value = ""; }
  };

  useEffect(() => {
    if (open) {
      if (existing) { setForm({ ...EMPTY, ...existing, amount: String(existing.amount ?? "") }); setAiDetected(false); }
      else { setForm({ ...EMPTY, next_due_date: defaultDate || "" }); setAiDetected(false); setPasteText(""); setLowConfidence(false); }
    }
  }, [open, existing, defaultDate]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const applyExtraction = (r, source) => {
    setForm((f) => ({
      ...f,
      name: r.name || "", merchant: r.merchant || "",
      category: r.category || "subscription", subcategory: r.subcategory || "",
      amount: r.amount != null ? String(r.amount) : "",
      frequency: r.frequency || "monthly",
      next_due_date: r.next_due_date || "",
      payment_source: r.payment_source || "",
      total_instalments: r.total_instalments != null ? String(r.total_instalments) : "",
      instalments_completed: r.instalments_completed != null ? String(r.instalments_completed) : "",
      notes: r.notes || "", detection_source: source, confidence: r.confidence || "low",
    }));
    setAiDetected(true);
    setLowConfidence(r.confidence === "low");
  };

  const extractText = async () => {
    if (!pasteText.trim()) { toast.error("Paste a message first."); return; }
    setExtracting(true);
    try {
      const { data } = await api.post("/extract", { text: pasteText });
      applyExtraction(data.result, "paste");
      toast.success("We found a possible recurring commitment. Please review and confirm.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setExtracting(false); }
  };

  const extractImage = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB."); return; }
    setExtracting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/extract/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyExtraction(data.result, "screenshot");
      toast.success("We found a possible recurring commitment. Please review and confirm.");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setExtracting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.name || !form.amount) { toast.error("Name and amount are required."); return; }
    setSaving(true);
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      total_instalments: form.total_instalments ? parseInt(form.total_instalments) : null,
      instalments_completed: form.instalments_completed !== "" ? parseInt(form.instalments_completed) : null,
      total_remaining_liability: form.total_remaining_liability ? parseFloat(form.total_remaining_liability) : null,
      user_share: form.user_share ? parseFloat(form.user_share) : null,
      people_sharing: form.people_sharing ? parseInt(form.people_sharing) : null,
      share_members: (form.share_members || []).filter((m) => m.name).map((m) => ({ name: m.name, amount: parseFloat(m.amount) || 0, status: m.status || "owed" })),
      next_due_date: form.next_due_date || null,
    };
    try {
      if (existing) await api.put(`/commitments/${existing.id}`, payload);
      else await api.post("/commitments", payload);
      toast.success(existing ? "Commitment updated." : "Commitment added.");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  const isEmi = form.category === "emi";
  const isShared = form.shared_individual === "shared" || form.category === "shared";

  const FormFields = (
    <div className="space-y-4">
      {aiDetected && (
        <div className="rounded-xl bg-mint-light/60 border border-mint/20 px-4 py-3 flex items-start gap-2" data-testid="ai-detected-banner">
          <Info size={16} className="text-mint mt-0.5" />
          <div className="text-sm text-ink">
            <span className="font-medium">AI-detected — please confirm.</span>{" "}
            {lowConfidence ? "Some details could not be identified. Please complete them manually." : "Review the details below before saving."}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label className="text-ink text-sm flex items-center gap-1">Commitment name <InfoTip title="Commitment name" text="A name that helps you recognise this payment. Examples: Car EMI, House Rent, Netflix, Home Loan, Health Insurance." /></Label>
          <Input value={form.name} onChange={(e) => set("name")(e.target.value)} className="mt-1 rounded-xl" data-testid="cf-name" /></div>
        <div><Label className="text-ink text-sm flex items-center gap-1">Merchant / provider <InfoTip title="Merchant / provider" text="The person or company you make this payment to. Examples: HDFC Bank, Netflix, LIC, your landlord, Airtel." /></Label>
          <Input value={form.merchant} onChange={(e) => set("merchant")(e.target.value)} className="mt-1 rounded-xl" data-testid="cf-merchant" /></div>
        <div><Label className="text-ink text-sm flex items-center gap-1">Amount (₹) <InfoTip title="Amount" text="How much you normally pay each time this commitment is due. Example: ₹14,500 for a monthly car EMI." /></Label>
          <Input type="number" value={form.amount} onChange={(e) => set("amount")(e.target.value)} className="mt-1 rounded-xl" data-testid="cf-amount" /></div>
        <div><Label className="text-ink text-sm flex items-center gap-1">Category <InfoTip title="Category" text="What kind of payment this is — it helps COMMITMENTT organise your commitments. Examples: Subscription, Loan/EMI, Rent, Insurance, Utilities, Education." /></Label>
          <Select value={form.category} onValueChange={set("category")}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="cf-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-ink text-sm flex items-center gap-1">Frequency <InfoTip title="Frequency" text="How often you make this payment. Examples: monthly, every 3 months, every 6 months, or yearly." /></Label>
          <Select value={form.frequency} onValueChange={set("frequency")}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="cf-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>{FREQUENCIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-ink text-sm flex items-center gap-1">Next due date <InfoTip title="Next due date" text="The next date this payment is made or becomes due. Example: if your EMI is deducted on the 10th, pick the upcoming 10th." /></Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="mt-1 w-full justify-start rounded-xl font-normal" data-testid="cf-duedate">
                <CalendarIcon size={15} className="mr-2 text-muted-foreground" />
                {form.next_due_date ? formatDate(form.next_due_date) : <span className="text-muted-foreground">Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={form.next_due_date ? new Date(form.next_due_date) : undefined}
                onSelect={(d) => set("next_due_date")(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "")} />
            </PopoverContent>
          </Popover></div>
        <div className="col-span-2"><Label className="text-ink text-sm flex items-center gap-1">Payment source <InfoTip title="Payment source" text="Where this payment comes from — the account or method you pay with, not the company receiving the money. Examples: HDFC Credit Card, SBI Bank Account, UPI." /></Label>
          <Input value={form.payment_source} onChange={(e) => set("payment_source")(e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. HDFC Credit Card, UPI" data-testid="cf-source" /></div>
        <div><Label className="text-ink text-sm flex items-center gap-1">Type <InfoTip title="Type" text="What kind of commitment this is. Essential — a must-pay you rely on, like rent, an EMI or insurance. Flexible — a nice-to-have you could pause or drop, like a subscription." /></Label>
          <Select value={form.essential_flexible} onValueChange={set("essential_flexible")}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="cf-essential"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="essential">Essential</SelectItem><SelectItem value="flexible">Flexible</SelectItem></SelectContent>
          </Select></div>
        <div><Label className="text-ink text-sm flex items-center gap-1">Ownership <InfoTip title="Ownership" text="Who is responsible for this commitment. Individual — it's yours alone. Shared — you split it with another person or household, and you can track your share." /></Label>
          <Select value={form.shared_individual} onValueChange={set("shared_individual")}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="cf-shared"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="individual">Individual</SelectItem><SelectItem value="shared">Shared</SelectItem></SelectContent>
          </Select></div>
        {isEmi && (
          <>
            <div><Label className="text-ink text-sm">Instalments completed</Label>
              <Input type="number" value={form.instalments_completed} onChange={(e) => set("instalments_completed")(e.target.value)} className="mt-1 rounded-xl" data-testid="cf-inst-done" /></div>
            <div><Label className="text-ink text-sm">Total instalments</Label>
              <Input type="number" value={form.total_instalments} onChange={(e) => set("total_instalments")(e.target.value)} className="mt-1 rounded-xl" data-testid="cf-inst-total" /></div>
            <div className="col-span-2"><Label className="text-ink text-sm">Total remaining liability (₹)</Label>
              <Input type="number" value={form.total_remaining_liability} onChange={(e) => set("total_remaining_liability")(e.target.value)} className="mt-1 rounded-xl" data-testid="cf-liability" /></div>
          </>
        )}
        {isShared && (
          <>
            <div><Label className="text-ink text-sm">Your share (₹)</Label>
              <Input type="number" value={form.user_share} onChange={(e) => set("user_share")(e.target.value)} className="mt-1 rounded-xl" data-testid="cf-share" /></div>
            <div><Label className="text-ink text-sm">People sharing</Label>
              <Input type="number" value={form.people_sharing} onChange={(e) => set("people_sharing")(e.target.value)} className="mt-1 rounded-xl" data-testid="cf-people" /></div>
            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <Label className="text-ink text-sm">Who owes what</Label>
                <button type="button" onClick={() => set("share_members")([...(form.share_members || []), { name: "", amount: "", status: "owed" }])}
                  className="text-xs text-mint" data-testid="cf-add-member">+ Add person</button>
              </div>
              <div className="space-y-2 mt-2">
                {(form.share_members || []).map((m, idx) => (
                  <div key={idx} className="flex gap-2 items-center" data-testid={`cf-member-${idx}`}>
                    <Input placeholder="Name" value={m.name} onChange={(e) => { const a = [...form.share_members]; a[idx] = { ...a[idx], name: e.target.value }; set("share_members")(a); }} className="rounded-xl flex-1" />
                    <Input placeholder="₹" type="number" value={m.amount} onChange={(e) => { const a = [...form.share_members]; a[idx] = { ...a[idx], amount: e.target.value }; set("share_members")(a); }} className="rounded-xl w-24" />
                    <button type="button" onClick={() => { const a = [...form.share_members]; a[idx] = { ...a[idx], status: a[idx].status === "paid" ? "owed" : "paid" }; set("share_members")(a); }}
                      className={`text-xs px-3 py-2 rounded-full shrink-0 ${m.status === "paid" ? "bg-calm-successbg text-calm-success" : "bg-calm-pendingbg text-calm-pending"}`}
                      data-testid={`cf-member-status-${idx}`}>{m.status === "paid" ? "Paid" : "Owes"}</button>
                    <button type="button" onClick={() => set("share_members")(form.share_members.filter((_, i) => i !== idx))} className="text-muted-foreground shrink-0">✕</button>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-2"><Label className="text-ink text-sm">Reimbursement reminder note</Label>
              <Input value={form.reminder_note} onChange={(e) => set("reminder_note")(e.target.value)} className="mt-1 rounded-xl" placeholder="e.g. Collect on payday" data-testid="cf-reminder-note" /></div>
          </>
        )}
        {existing && (
          <div className="col-span-2"><Label className="text-ink text-sm">Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="cf-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="paused">Paused</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
            </Select></div>
        )}
        <div className="col-span-2"><Label className="text-ink text-sm flex items-center gap-1">Notes <InfoTip title="Notes" text="Optional information you'd like to remember about this commitment. Example: “EMI ends in March 2028.”" /></Label>
          <Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} className="mt-1 rounded-xl" data-testid="cf-notes" /></div>
      </div>
      <Button onClick={save} disabled={saving} className="w-full rounded-full bg-navy text-white hover:bg-navy-light h-11" data-testid="cf-save">
        {saving ? "Saving…" : (existing ? "Save changes" : "Save commitment")}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle className="font-heading text-ink flex items-center gap-1.5">{existing ? "Edit commitment" : "Add commitment"}{!existing && <InfoTip title="Ways to add a commitment" text="Manual — enter it yourself. Paste — paste a bank/merchant message and we'll pull out the details. Photo — upload a screenshot for us to read. Statement — upload a PDF statement to extract details. For Paste, Photo and Statement, always review the extracted details before saving." />}</DialogTitle></DialogHeader>
        {existing ? FormFields : (
          <Tabs defaultValue="manual">
            <TabsList className="grid grid-cols-4 rounded-full bg-secondary">
              <TabsTrigger value="manual" className="rounded-full text-xs" data-testid="cf-tab-manual"><PenLine size={13} className="mr-1" />Manual</TabsTrigger>
              <TabsTrigger value="paste" className="rounded-full text-xs" data-testid="cf-tab-paste"><Sparkles size={13} className="mr-1" />Paste</TabsTrigger>
              <TabsTrigger value="upload" className="rounded-full text-xs" data-testid="cf-tab-upload"><Upload size={13} className="mr-1" />Photo</TabsTrigger>
              <TabsTrigger value="pdf" className="rounded-full text-xs" data-testid="cf-tab-pdf"><FileText size={13} className="mr-1" />Statement</TabsTrigger>
            </TabsList>
            <TabsContent value="manual" className="mt-4">{FormFields}</TabsContent>
            <TabsContent value="paste" className="mt-4 space-y-3">
              <Label className="text-ink text-sm">Paste a bank or merchant message</Label>
              <Textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={4} className="rounded-xl"
                placeholder="e.g. Your Netflix membership of Rs 649 will renew on 15 Jun…" data-testid="cf-paste-text" />
              <Button onClick={extractText} disabled={extracting} className="w-full rounded-full bg-mint text-white hover:bg-mint/90 h-11" data-testid="cf-extract-text">
                {extracting ? <><Loader2 size={16} className="mr-2 animate-spin" />Reading…</> : <><Sparkles size={16} className="mr-2" />Extract with AI</>}
              </Button>
              <p className="text-xs text-muted-foreground">Your message is sent to our AI provider (OpenAI, via Emergent) only to extract details, and is never stored by COMMITMENTT. You'll confirm everything before saving.</p>
              {aiDetected && <div className="pt-2 border-t border-border">{FormFields}</div>}
            </TabsContent>
            <TabsContent value="upload" className="mt-4 space-y-3">
              <Label className="text-ink text-sm">Upload a screenshot (PNG, JPEG, WEBP · under 5 MB)</Label>
              <button onClick={() => fileRef.current?.click()} disabled={extracting}
                className="w-full rounded-xl border-2 border-dashed border-border py-10 flex flex-col items-center gap-2 hover:border-mint transition-colors" data-testid="cf-upload-zone">
                {extracting ? <Loader2 size={22} className="animate-spin text-mint" /> : <Upload size={22} className="text-muted-foreground" />}
                <span className="text-sm text-slatey">{extracting ? "Reading screenshot…" : "Tap to choose an image"}</span>
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => extractImage(e.target.files?.[0])} data-testid="cf-file-input" />
              <p className="text-xs text-muted-foreground">The image is sent to our AI provider (OpenAI, via Emergent) for extraction, then permanently deleted immediately — COMMITMENTT never stores it.</p>
              {aiDetected && <div className="pt-2 border-t border-border">{FormFields}</div>}
            </TabsContent>
            <TabsContent value="pdf" className="mt-4 space-y-3">
              <Label className="text-ink text-sm">Upload a financial statement (PDF · under 10 MB)</Label>
              <p className="text-xs text-slatey">Loan / EMI schedules, insurance premiums, credit-card or financing statements. We extract what we can — anything unclear is marked <span className="font-medium">Not detected</span> for you to complete. Nothing is saved until you confirm.</p>
              <button onClick={() => pdfRef.current?.click()} disabled={extracting}
                className="w-full rounded-xl border-2 border-dashed border-border py-10 flex flex-col items-center gap-2 hover:border-mint transition-colors" data-testid="cf-pdf-zone">
                {extracting ? <Loader2 size={22} className="animate-spin text-mint" /> : <FileText size={22} className="text-muted-foreground" />}
                <span className="text-sm text-slatey">{extracting ? "Reading your statement…" : "Tap to choose a PDF"}</span>
              </button>
              <input ref={pdfRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => extractPdf(e.target.files?.[0])} data-testid="cf-pdf-input" />
              <p className="text-xs text-muted-foreground">The PDF text is sent to our AI provider (OpenAI, via Emergent) only to extract commitment details, and the file is never stored by COMMITMENTT.</p>
              {aiDetected && <div className="pt-2 border-t border-border">{FormFields}</div>}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
